import {
  POOLS_MAP,
  PoolName,
  TRANSACTION_TYPES,
  Token,
  CRO,
  WCRO,
  PoolTypes,
} from "../constants"
import { enqueueToast } from "../components/Toastify"
import { formatDeadlineToNumber, getContract } from "../utils"
import {
  useAllContracts,
  useLPTokenContract,
  useSwapContract,
  useWethContract,
} from "./useContract"
import { useDispatch, useSelector } from "react-redux"
import { AppState } from "../state"
import { BigNumber } from "@ethersproject/bignumber"
import { Erc20 } from "../../types/ethers-contracts/Erc20"
import { GasPrices } from "../state/user"
import META_SWAP_ABI from "../constants/abis/metaSwap.json"
import { MetaSwap } from "../../types/ethers-contracts/MetaSwap"
import { NumberInputState } from "../utils/numberInputState"
import { SwapFlashLoanNoWithdrawFee } from "../../types/ethers-contracts/SwapFlashLoanNoWithdrawFee"
import { parseUnits } from "@ethersproject/units"
import { subtractSlippage } from "../utils/slippage"
import { updateLastTransactionTimes } from "../state/application"
import { useActiveWeb3React } from "."
import { useMemo } from "react"
import { useTransactionModal } from "src/providers/TransactionModalProvider"
import { TransactionSummaryData } from "src/components/TransactionSummary"
import { useCheckAndApproveBatchTokenForTrade } from "./useCheckAndApproveBatchTokenForTrade"

interface ApproveAndDepositStateArgument {
  [tokenSymbol: string]: NumberInputState
}

function getTokenAmountInput(
  state: ApproveAndDepositStateArgument,
  token: Token,
) {
  return token.symbol === WCRO.symbol
    ? state[CRO.symbol].valueSafe
    : state[token.symbol].valueSafe
}

export function useApproveAndDeposit(
  poolName: PoolName,
): (
  state: ApproveAndDepositStateArgument,
  summary: TransactionSummaryData,
  shouldDepositWrapped?: boolean,
) => Promise<void> {
  const dispatch = useDispatch()
  const swapContract = useSwapContract(poolName)
  const wcroContract = useWethContract(WCRO)
  const lpTokenContract = useLPTokenContract(poolName)
  const tokenContracts = useAllContracts()
  const { account, chainId, library } = useActiveWeb3React()
  const { addTransactions } = useTransactionModal()
  const { gasStandard, gasFast, gasInstant } = useSelector(
    (state: AppState) => state.application,
  )
  const {
    slippageCustom,
    slippageSelected,
    gasPriceSelected,
    gasCustom,
    transactionDeadlineCustom,
    transactionDeadlineSelected,
    infiniteApproval,
  } = useSelector((state: AppState) => state.user)
  const POOL = POOLS_MAP[poolName]
  const metaSwapContract = useMemo(() => {
    if (POOL.metaSwapAddresses && chainId && library) {
      return getContract(
        POOL.metaSwapAddresses?.[chainId],
        META_SWAP_ABI,
        library,
        account ?? undefined,
      ) as MetaSwap
    }
    return null
  }, [chainId, library, POOL.metaSwapAddresses, account])
  const checkAndApproveBatchTokenForTrade =
    useCheckAndApproveBatchTokenForTrade()

  return async function approveAndDeposit(
    state: ApproveAndDepositStateArgument,
    summary: TransactionSummaryData,
    shouldDepositWrapped = false,
  ): Promise<void> {
    try {
      if (!account || !chainId) throw new Error("Wallet must be connected")
      if (
        !swapContract ||
        !lpTokenContract ||
        (POOL.type === PoolTypes.CRO && !wcroContract) ||
        (shouldDepositWrapped && !metaSwapContract)
      )
        throw new Error("Swap contract is not loaded")

      const poolTokens = shouldDepositWrapped
        ? (POOL.underlyingPoolTokens as Token[])
        : POOL.poolTokens
      const effectiveSwapContract = shouldDepositWrapped
        ? (metaSwapContract as MetaSwap)
        : swapContract

      let gasPriceUnsafe: string | number | undefined
      if (gasPriceSelected === GasPrices.Custom) {
        gasPriceUnsafe = gasCustom?.valueSafe
      } else if (gasPriceSelected === GasPrices.Fast) {
        gasPriceUnsafe = gasFast
      } else if (gasPriceSelected === GasPrices.Instant) {
        gasPriceUnsafe = gasInstant
      } else {
        gasPriceUnsafe = gasStandard
      }
      const gasPrice = parseUnits(
        gasPriceUnsafe ? String(gasPriceUnsafe) : "45",
        9,
      )

      // For each token being deposited, check the allowance and approve it if necessary
      await checkAndApproveBatchTokenForTrade(
        poolTokens.map((token) => {
          const spendingValue = BigNumber.from(
            getTokenAmountInput(state, token),
          )
          const tokenContract = tokenContracts?.[token.symbol] as Erc20
          return {
            srcTokenContract: tokenContract,
            spenderAddress: effectiveSwapContract.address,
            ownerAddress: account,
            spendingValue,
            infiniteApproval,
            gasPrice,
            callbacks: {
              onTransactionError: () => {
                throw new Error("Approval failed")
              },
            },
            token,
          }
        }),
      )

      const isFirstTransaction = (await lpTokenContract.totalSupply()).isZero()
      let minToMint: BigNumber
      if (isFirstTransaction) {
        minToMint = BigNumber.from("0")
      } else {
        minToMint = await (
          effectiveSwapContract as SwapFlashLoanNoWithdrawFee
        ).calculateTokenAmount(
          poolTokens.map((token) => getTokenAmountInput(state, token)),
          true, // deposit boolean
        )
      }

      minToMint = subtractSlippage(minToMint, slippageSelected, slippageCustom)
      const deadline = formatDeadlineToNumber(
        transactionDeadlineSelected,
        transactionDeadlineCustom,
      )

      const txnAmounts = poolTokens.map((token) =>
        getTokenAmountInput(state, token),
      )
      const txnDeadline = Math.round(
        new Date().getTime() / 1000 + 60 * deadline,
      )

      const swapFlashLoanContract =
        effectiveSwapContract as SwapFlashLoanNoWithdrawFee
      const addLiqTxt = async () => {
        return await swapFlashLoanContract?.addLiquidity(
          txnAmounts,
          minToMint,
          txnDeadline,
        )
      }
      const transactions = [addLiqTxt]
      if (poolTokens.indexOf(WCRO) >= 0) {
        const croAmount = BigNumber.from(getTokenAmountInput(state, CRO))
        if (croAmount.gt(0)) {
          const wrapTxt = async () => {
            return await (
              wcroContract as NonNullable<
                typeof wcroContract // we already check for nonnull above
              >
            ).deposit({
              value: getTokenAmountInput(state, WCRO),
            })
          }
          transactions.unshift(wrapTxt)
        }
      }
      await addTransactions(transactions, summary)

      dispatch(
        updateLastTransactionTimes({
          [TRANSACTION_TYPES.DEPOSIT]: Date.now(),
        }),
      )
      return Promise.resolve()
    } catch (e) {
      console.error(e)
      enqueueToast(
        "error",
        e instanceof Error
          ? e.message
          : "Liquidity addition failed, your tokens have been reverted",
        "Failed",
      )
    }
  }
}
